import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { catchError, retry } from 'rxjs/operators';
import isMatch from 'lodash-es/isMatch';
import { Observable, throwError, timer } from 'rxjs';
import browserHelper from '@utils/browser-helper';
import { storage } from '@utils/storage.service';
import { AuthService } from '@app/shared/services/auth.service';
import { logger } from '@utils/logger.service';
import { IFBWindow } from '@app/models/window-namespace';
import { ToastService } from '@app/shared/services/toast/toast.abstract.service';
import { IS_MFE_MODE } from '@app/shared/tokens/is-mfe-mode';

function expectedError(
  request: HttpRequest<any>,
  response: HttpErrorResponse,
  urlRegExp: RegExp,
  method: string,
  status: number,
  code?: string,
  params?
) {
  params = params || {};

  const url = response.url;
  const isCodeMatching = code ? response.error && response.error.code === code : true;

  return (
    urlRegExp.test(url) &&
    request.method === method &&
    response.status === status &&
    isCodeMatching &&
    isMatch(request.params, params)
  );
}

export function globalErrorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const ifbWindow = inject(IFBWindow);
  const auth = inject(AuthService);
  const toast = inject(ToastService);
  const isMfe = inject(IS_MFE_MODE);

  const showApiError = (apiErrorKey: string): void => {
    toast.showError('api-error.' + apiErrorKey);
  };

  return next(req).pipe(
    retry({
      count: 2,
      delay: error => {
        if (expectedError(req, error, /.*/, 'GET', 0)) {
          return timer(1000);
        }
        return throwError(() => error);
      }
    }),
    catchError((res: HttpErrorResponse) => {
      const key = res ? res.error?.code || res.error?.error : undefined;

      // if MFE, handle 401 with mfeTokenRefreshInterceptor
      if (isMfe && res?.status === HttpStatusCode.Unauthorized) {
        return throwError(() => res);
      }
      // do not notify error if this is an error to get file, it's expected and handled in controller
      if (expectedError(req, res, /.*\/files\/.*/, 'GET', 500, '0000')) {
        return throwError(() => res);
      }

      // do not notify error when user is using incomplete token (first sign in with profile incomplete)
      if (expectedError(req, res, /.*\/v1\/account/, 'GET', 400, '40033')) {
        return throwError(() => res);
      }

      // do not notify if color-theme not-found
      if (expectedError(req, res, /.*\/color_theme/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      // do not notify error if this is the email change confirmation
      if (expectedError(req, res, /.*\/companies\/.*/, 'GET', 404, '4041')) {
        logger.error('Company does not exist:', res.error);
        return throwError(() => res);
      }

      // do not notify error if custom domain metadata not found
      if (expectedError(req, res, /.*\/metadata/, 'GET', 404, '4040')) {
        return throwError(() => res);
      }

      // do not notify error if this is the email change confirmation
      if (expectedError(req, res, /.*\/email\/.*/, 'POST', 400, '4002')) {
        return throwError(() => res);
      }

      // do not notify error if this is the expired social token
      if (expectedError(req, res, /.*\/shares/, 'POST', 400, '4007')) {
        return throwError(() => res);
      }

      // do not notify error if job application does not exist
      if (expectedError(req, res, /.*\/applications/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      if (expectedError(req, res, /.*\/applications/, 'GET', 404, '4043')) {
        return throwError(() => res);
      }

      // do not notify error if there was an internal problem when retrieving job applications
      if (expectedError(req, res, /.*\/applications/, 'GET', 500, '0000')) {
        return throwError(() => res);
      }

      // do not notify when second job application step fails due to data not ready yet, retry in a few seconds
      if (expectedError(req, res, /.*\/applications/, 'POST', 400, '4050')) {
        return throwError(() => res);
      }

      // do not notify error if applicant does not exist
      if (expectedError(req, res, /.*\/applicants/, 'GET', 404, '4042')) {
        return throwError(() => res);
      }

      // do not notify error if applicant does not exist
      if (expectedError(req, res, /.*\/applicants/, 'GET', 404, '4040')) {
        return throwError(() => res);
      }

      // do not notify error if job does not exist
      if (expectedError(req, res, /.*\/jobs\/.*/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      // do not notify 40011 error when deleting user account
      if (expectedError(req, res, /.*\/account\/delete/, 'POST', 400, '40011')) {
        return throwError(() => res);
      }

      // do not notify 400 error when incognito user tries to access the Leaderboard
      if (
        expectedError(req, res, /.*\/leaderboard/, 'GET', 400, '0000') &&
        res.error?.message === 'leaderboard not available in incognito mode'
      ) {
        return throwError(() => res);
      }

      // do not notify if Find me button couldn't find a user
      if (expectedError(req, res, /.*\/leaderboard/, 'GET', 500, '5000')) {
        return throwError(() => res);
      }

      // do not notify error on the public invitation page
      if (expectedError(req, res, /.*\/invitations/, 'POST', 400, '4004', { single: 'true' })) {
        return throwError(() => res);
      }

      // do not notify not-found invitationLink error because that is handled in register/index.js
      if (expectedError(req, res, /.*\/invitations\/.*/, 'PUT', 404, '4046')) {
        return throwError(() => res);
      }

      // do not notify if integration signals additional payment needed
      if (expectedError(req, res, /.*\/integrations\/.*/, 'GET', 402, '4020')) {
        return throwError(() => res);
      }

      // do not notify not-found registration page branding
      if (expectedError(req, res, /.*\/registration_page/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      // do not notify if registration_form is missing
      if (expectedError(req, res, /.*\/registration_form/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      // do not notify if application response is missing
      if (expectedError(req, res, /.*\/response/, 'GET', 404, '0000')) {
        return throwError(() => res);
      }

      // do not notify if SAML parsing has errors
      if (expectedError(req, res, /.*\/parse_metadata/, 'POST', 400, '4000')) {
        return throwError(() => res);
      }

      // do not notify if duplicated referral submitted
      if (expectedError(req, res, /.*\/candidate\/referral/, 'POST', 400, '40029')) {
        return throwError(() => res);
      }

      // do not notify if referral token is no longer valid
      if (
        expectedError(req, res, /.*\/candidate\/referralinfo/, 'GET', 401, '4010') ||
        expectedError(req, res, /.*candidate\/v1\/referral/, 'GET', 401, '4010') ||
        expectedError(req, res, /.*candidate\/v1\/share/, 'GET', 401, '4010')
      ) {
        return throwError(() => res);
      }

      // do not notify error if job application email receiver does not exist
      if (
        expectedError(req, res, /.*\/jobapplication_email_receiver/, 'GET', 404, '0000') ||
        expectedError(req, res, /.*\/jobapplication_email_receiver/, 'GET', 404, '4040')
      ) {
        logger.error('Company JobApplication Email Receiver does not exist:', res.error);
        return throwError(() => res);
      }

      // do not notify if greenhouse integration does not exist
      if (expectedError(req, res, /.*\/integrations\/greenhouse/, 'GET', 404, '4040')) {
        logger.error('Company JobApplication Email Receiver does not exist:', res.error);
        return throwError(() => res);
      }

      // do not notify if smartrecruiters integration does not exist
      if (expectedError(req, res, /.*\/integrations\/smartrecruiters/, 'GET', 404, '4040')) {
        logger.error('Company JobApplication Email Receiver does not exist:', res.error);
        return throwError(() => res);
      }

      // do not notify if customer profile checks fail
      if (expectedError(req, res, /.*\/connect/, 'POST', 400, '4000')) {
        return throwError(() => res);
      }

      if (expectedError(req, res, /.*\/profile/, 'PATCH', 400, '4000')) {
        return throwError(() => res);
      }

      // do not notify when obtaining token fails due to data not ready yet, retry in a few seconds
      if (expectedError(req, res, /.*\/oauth\/token/, 'POST', 400)) {
        return throwError(() => res);
      }

      if (expectedError(req, res, /.*\/user\/dashboards/, 'GET', 403, '4030')) {
        return throwError(() => res);
      }

      // mfa code error is shown in the form
      if (expectedError(req, res, /.*\/accounts\/.+\/devices\/.+\/enable/, 'POST', 400, '40000')) {
        return throwError(() => res);
      }

      // do not notify if specified referral does not have a feedback
      if (expectedError(req, res, /.*\/feedback/, 'GET', 404, '4040')) {
        return throwError(() => res);
      }

      // redirect to login
      if (expectedError(req, res, /.*/, 'GET', 401, '4010')) {
        if (browserHelper.isInWrapper) {
          auth.endSession();
        } else {
          auth.restoreSessionOnError();
        }

        return throwError(() => res);
      }

      // Token of active user was blacklisted so user should be logged out
      if (res.status === 403 && key === '4030') {
        showApiError(key);
        storage.removeItem('token');
        setTimeout(() => {
          auth.endSession(ifbWindow.location.pathname);
        }, 3000);
      } else if (res?.error?.errors?.length) {
        showApiError(key + '.' + res.error?.errors[0]);
        // Handle Authentication error message on suspended Company account
      } else if (
        key === 'access_denied' &&
        res.error.error_description &&
        res.error?.error_description.substr(0, 11) === 'ERROR_CODE:'
      ) {
        showApiError(res.error?.error_description.replace('ERROR_CODE:', ''));
        // Handle Authentication error message on user token blacklisted
      } else if (
        key === 'invalid_grant' &&
        res.error?.error_description &&
        res.error?.error_description === 'User is disabled'
      ) {
        showApiError('4030');
      } else if (browserHelper.isInWrapper && key === 'invalid_grant') {
        // don't show bad credentials warning inside mobile app.
      } else {
        showApiError(key);
      }

      if (res.error && res.error.developerMessage) {
        logger.error('API error:', res.error.developerMessage);
      }

      logger.error('API returned an error:', res.error);

      return throwError(() => res);
    })
  );
}
